// Shared helpers for embedding a CSD (TCSD) workflow inside a Teacher
// Dashboard iframe, per the `embed=td-sensai` launch/lifecycle contract
// documented in alpaca-tcsd's docs/sensai-embed.md.

export const CSD_EMBEDDED_WORKFLOW_IDS = [
  "reset-task",
  "add-child",
  "remove-child",
  "create-class",
  "remove-class",
  "request-access",
  "request-access-other-teacher",
  "swap-students-results",
  "fix-duplicate-child",
  "fix-duplicate-results",
  "move-transfer-children",
  "update-teacher-roles",
  "remove-colleague-access",
  "remove-teacher-from-school",
  "invite-teacher-to-platform",
  "send-account-setup-link",
  "send-password-reset-link",
  "update-user-email",
  "rename-class",
  "assign-alpaca-lead",
  "assign-screener",
  "extend-trial",
  "restore-child-results",
  "send-whatsapp-group-invite",
  "provide-login-bypass",
  "activate-teacher-account",
  "merge-classes",
  "remove-teacher-assignment",
  "request-early-platform-access",
  "request-invoice",
  "trial-access-status",
  "fix-duplicate-child-results",
  "export-assessment-data",
  "make-results-visible",
  "escalate-technical-issue",
  "resolve-login-issue",
  "rename-user",
  "offer-to-add-teacher",
  "offer-meeting",
  "screening-guidance-resources",
  "escalate-feature-request",
];

export const CSD_EMBEDDED_WORKFLOW_ALLOWLIST = new Set(CSD_EMBEDDED_WORKFLOW_IDS);

export const CSD_WORKFLOW_ALIASES = {
  "assign-alpaca-lead-role": "assign-alpaca-lead",
};

export const WORKFLOW_MESSAGE_TARGET = "td-sensai";
export const WORKFLOW_READY_TIMEOUT_MS = 15000;

export function getCanonicalWorkflowId(workflow = "") {
  return CSD_WORKFLOW_ALIASES[workflow] || workflow;
}

export function isEmbeddedWorkflowSupported(workflow) {
  return CSD_EMBEDDED_WORKFLOW_ALLOWLIST.has(getCanonicalWorkflowId(workflow));
}

export function createWorkflowLaunchId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `workflow_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function isLocalCsdHostname(hostname) {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

export function getCsdBaseUrl() {
  const csdBaseUrl = (process.env.REACT_APP_TCSD_URL || "")
    .trim()
    .replace(/^['"]|['"]$/g, "");

  if (!csdBaseUrl) {
    return "";
  }

  try {
    const url = new URL(csdBaseUrl);

    if (
      window.location.protocol === "https:" &&
      url.protocol === "http:" &&
      !isLocalCsdHostname(url.hostname)
    ) {
      url.protocol = "https:";
    }

    return url.toString().replace(/\/$/, "");
  } catch (err) {
    return csdBaseUrl.replace(/\/$/, "");
  }
}

export function getCsdOrigin() {
  const csdBaseUrl = getCsdBaseUrl();

  try {
    return new URL(csdBaseUrl).origin;
  } catch (err) {
    return "";
  }
}

// The `embed=td-portal` contract shows the full CSD portal (every workflow,
// search included) inside an iframe with TD chrome hidden. Unlike the
// single-workflow `embed=td-sensai` contract, it takes no workflow/launchId/
// parentOrigin and has no postMessage lifecycle - CSD just renders normally
// with navigation hidden.
export function buildCsdPortalUrl(schoolId) {
  const csdBaseUrl = getCsdBaseUrl();
  const token = localStorage.getItem("token");

  if (!csdBaseUrl || !schoolId || !token) {
    return "";
  }

  const params = new URLSearchParams({
    token,
    schoolId,
    source: "td",
    embed: "td-portal",
  });

  return `${csdBaseUrl}/masterdashboard?${params.toString()}`;
}

function appendWorkflowParam(params, key, value) {
  if (value === null || value === undefined || value === "") {
    return;
  }

  if (Array.isArray(value) && value.length === 0) {
    return;
  }

  if (
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  ) {
    return;
  }

  params.set(
    key,
    typeof value === "string" ? value : JSON.stringify(value)
  );
}

export function buildCsdWorkflowUrl(workflowPayload, schoolId, options = {}) {
  const csdBaseUrl = getCsdBaseUrl();
  const token = localStorage.getItem("token");
  const workflow = getCanonicalWorkflowId(workflowPayload?.workflow);
  const { embed = false, launchId = "", parentOrigin = "" } = options;

  if (!csdBaseUrl || !workflow || !schoolId || !token) {
    return "";
  }

  const params = new URLSearchParams({
    token,
    schoolId,
    source: "td",
    workflow,
  });

  if (embed) {
    params.set("embed", "td-sensai");
    appendWorkflowParam(params, "launchId", launchId);
    appendWorkflowParam(params, "parentOrigin", parentOrigin);
  }

  const prefill = workflowPayload?.prefill || {};
  Object.entries(prefill).forEach(([key, value]) => {
    appendWorkflowParam(params, `prefill.${key}`, value);
  });

  appendWorkflowParam(params, "studentName", prefill.studentName);
  appendWorkflowParam(params, "className", prefill.className);
  appendWorkflowParam(params, "classLevel", prefill.classLevel);
  appendWorkflowParam(params, "newClassName", prefill.newClassName);
  appendWorkflowParam(params, "classNames", prefill.classNames);
  appendWorkflowParam(params, "teacherName", prefill.teacherName);
  appendWorkflowParam(params, "email", prefill.email);
  appendWorkflowParam(params, "currentEmail", prefill.currentEmail);
  appendWorkflowParam(params, "newEmail", prefill.newEmail);
  appendWorkflowParam(params, "secondTeacherName", prefill.secondTeacherName);
  appendWorkflowParam(params, "schoolName", prefill.schoolName);
  appendWorkflowParam(params, "reason", prefill.reason);
  appendWorkflowParam(params, "note", prefill.note);
  appendWorkflowParam(params, "trialEndsOn", prefill.trialEndsOn);
  appendWorkflowParam(params, "recommendedAction", prefill.recommendedAction);
  appendWorkflowParam(params, "studentNames", prefill.studentNames);
  appendWorkflowParam(params, "fromClassName", prefill.fromClassName);
  appendWorkflowParam(params, "toClassName", prefill.toClassName);
  appendWorkflowParam(params, "mobileNumber", prefill.mobileNumber);
  appendWorkflowParam(params, "whatsappGroupName", prefill.whatsappGroupName);
  appendWorkflowParam(params, "deliveryMethod", prefill.deliveryMethod);
  appendWorkflowParam(params, "issueDescription", prefill.issueDescription);
  appendWorkflowParam(params, "prefill.invoiceRequestType", prefill.invoiceRequestType);
  appendWorkflowParam(params, "prefill.recipientEmail", prefill.recipientEmail);
  appendWorkflowParam(params, "prefill.poNumber", prefill.poNumber);
  appendWorkflowParam(params, "prefill.meetingTopic", prefill.meetingTopic);
  appendWorkflowParam(params, "student_name", prefill.student_name);
  appendWorkflowParam(params, "class_name", prefill.class_name);
  appendWorkflowParam(params, "new_class_name", prefill.new_class_name);
  appendWorkflowParam(params, "task_name", prefill.task_name);
  appendWorkflowParam(params, "teacher_name", prefill.teacher_name);
  appendWorkflowParam(params, "second_teacher_name", prefill.second_teacher_name);
  appendWorkflowParam(
    params,
    "reference_number",
    workflowPayload?.reference?.reference_number
  );

  return `${csdBaseUrl}/masterdashboard?${params.toString()}`;
}
